import React, { useEffect, useState } from "react";
import { Users, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const TIER_STYLES = {
  high: { color: "#1A422D", bg: "#E9F0EB", border: "#1A422D", label: "High Demand" },
  medium: { color: "#8a6f33", bg: "#FBF6E8", border: "#B59A5A", label: "Medium Demand" },
  low: { color: "#5A5A5A", bg: "#F3F1E9", border: "#C9C7BD", label: "Low Demand" },
  none: { color: "#7A7A7A", bg: "#F9F8F4", border: "#E5E3D8", label: "No interest yet" },
};

/**
 * DemandMeter — admin-only widget on the order detail page.
 * Shows how many unique members have wishlisted or ordered the same item,
 * with a star rating, color-coded tier, and an expandable interested list.
 */
export default function DemandMeter({ orderId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/admin/demand/${orderId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => { cancelled = true; };
  }, [orderId]);

  if (error) {
    return (
      <div className="surface-card p-5 text-sm text-[#8a1a1a]">Demand: {error}</div>
    );
  }
  if (!data) {
    return (
      <div className="surface-card p-5 text-sm text-[#7A7A7A]">Calculating demand…</div>
    );
  }

  const tier = TIER_STYLES[data.tier] || TIER_STYLES.none;

  return (
    <section className="surface-card p-6 sm:p-7" data-testid="demand-meter">
      <div className="overline">Demand Meter</div>
      <div className="mt-2 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-0.5" aria-label={`${data.stars} of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={22}
                fill={n <= data.stars ? tier.color : "transparent"}
                color={n <= data.stars ? tier.color : "#C9C7BD"}
                strokeWidth={1.5}
              />
            ))}
          </div>
          <span
            className="text-xs uppercase tracking-[0.18em] font-semibold px-3 py-1 border"
            style={{ color: tier.color, background: tier.bg, borderColor: tier.border }}
          >
            {tier.label}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[#1A1A1A]">
          <Users size={16} className="text-[#1A422D]" />
          <span className="font-serif text-2xl" data-testid="demand-count">
            {data.count}
          </span>
          <span className="text-sm text-[#4A4A4A]">
            Member{data.count === 1 ? "" : "s"} Interested
          </span>
        </div>
      </div>
      <p className="text-xs text-[#7A7A7A] mt-3">
        Across all active orders and wishlists for "{data.item_name}".
      </p>
      {data.count > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="demand-toggle"
            className="mt-4 text-xs uppercase tracking-wider text-[#1A422D] hover:underline"
          >
            {open ? "Hide" : "View"} Interested Members →
          </button>
          {open && (
            <ul className="mt-3 border-t border-[#E5E3D8] divide-y divide-[#E5E3D8]" data-testid="demand-members">
              {data.members.map((m) => (
                <li key={m.user_id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-serif text-base text-[#1A1A1A]">{m.name}</div>
                    <div className="text-xs text-[#7A7A7A]">
                      {m.email}{m.home_club ? ` · ${m.home_club}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
